import NotFound from '../NotFound';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPostBySlug, blogPosts } from './data';
import { resolveBlogAuthor } from './blogAuthors';
import { useEffect, useMemo } from 'react';
import { ArticleSections } from './BlogSections';
import { isComparisonPost } from './blogSectionUtils';
import SEO, {
  articleJsonLd,
  breadcrumbJsonLd,
  DEFAULT_OG_IMAGE,
  faqJsonLd,
  itemListJsonLd,
  SITE_URL,
} from '../../components/SEO';

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const DEFAULT_HERO = '/images/route-planner-feature.png';

/** Absolute URLs (e.g. the CDN) are already canonical; site-relative paths need SITE_URL. */
function toAbsoluteUrl(path: string) {
  return /^https?:\/\//.test(path) ? path : `${SITE_URL}${path}`;
}

function authorInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Match blog index category pills (customer stories → rose, etc.). */
function postCategoryBadgeClass(category: string): string {
  const c = category.toLowerCase();
  if (c.includes('customer') || c.includes('experience') || c.includes('story') || c.includes('cx'))
    return 'bg-rose-50 text-rose-800 border border-rose-100';
  if (
    c.includes('compar') ||
    c.includes('alternative') ||
    c.includes('vs') ||
    c.includes('route planner')
  )
    return 'bg-violet-50 text-violet-800 border border-violet-100';
  if (
    c.includes('delivery') ||
    c.includes('last mile') ||
    c.includes('dispatch') ||
    c.includes('fleet')
  )
    return 'bg-sky-50 text-sky-800 border border-sky-100';
  if (c.includes('route') || c.includes('optimization'))
    return 'bg-purple-50 text-purple-800 border border-purple-100';
  if (c.includes('integration') || c.includes('ai') || c.includes('automation'))
    return 'bg-emerald-50 text-emerald-800 border border-emerald-100';
  return 'bg-blue-50 text-blue-800 border border-blue-100';
}

type BlogPostLocationState = { blogListSearch?: string };

export default function BlogPostPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const post = slug ? getPostBySlug(slug) : undefined;

  const blogListSearch = (location.state as BlogPostLocationState | null)?.blogListSearch;
  const backToBlogTo =
    blogListSearch && blogListSearch.length > 0 ? `/blog?${blogListSearch}` : '/blog';

  const tocItems = useMemo(() => {
    if (!post?.sections?.length) return [];
    const items: { id: string; text: string }[] = [];
    post.sections.forEach((s, i) => {
      if (s.type === 'h2') {
        items.push({ id: `blog-heading-${i}`, text: s.text });
      }
    });
    return items;
  }, [post?.sections]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return <NotFound />;
  }

  const displayAuthor = resolveBlogAuthor(post.slug, post.author);

  const heroImage = post.image ?? DEFAULT_HERO;
  const heroIsSvg = heroImage.endsWith('.svg');
  const heroInset = post.heroImageInset === true && !heroIsSvg;
  const heroShouldContain = heroIsSvg || post.heroImageFit === 'contain';
  const ogImage = post.ogImage
    ? toAbsoluteUrl(post.ogImage)
    : post.image && !post.image.endsWith('.svg')
      ? toAbsoluteUrl(post.image)
      : DEFAULT_OG_IMAGE;
  const hasSections = post.sections && post.sections.length > 0;
  const introText = post.intro ?? post.excerpt;
  const isComparison = isComparisonPost(post.category);

  /** Related posts: same category first, then newest, top 3. */
  const relatedPosts = blogPosts
    .filter((p) => p.slug !== post.slug)
    .map((p) => ({ p, sameCat: p.category === post.category ? 1 : 0, t: new Date(p.date).getTime() }))
    .sort((a, b) => b.sameCat - a.sameCat || b.t - a.t)
    .slice(0, 3)
    .map((x) => x.p);
  const postUrl = `${SITE_URL}/blog/${post.slug}`;
  const jsonLdBlocks: Record<string, unknown>[] = [
    articleJsonLd({
      type: 'BlogPosting',
      headline: post.title,
      description: post.seo?.metaDescription ?? post.excerpt,
      url: postUrl,
      image: ogImage,
      datePublished: post.date,
      dateModified: post.updated,
      authorName: displayAuthor,
      section: post.category,
    }),
    breadcrumbJsonLd([
      { name: t('common.breadcrumb.home'), url: 'https://geofleet.ai' },
      { name: t('common.breadcrumb.blog'), url: 'https://geofleet.ai/blog' },
      { name: post.title, url: postUrl },
    ]),
  ];
  if (post.faq?.length) {
    jsonLdBlocks.push(faqJsonLd(post.faq));
  }
  if (post.itemList?.length) {
    jsonLdBlocks.push(itemListJsonLd(post.title, post.itemList));
  }

  return (
    <div className="pt-24 bg-white">
      <SEO
        title={post.seo?.documentTitle ?? post.title}
        description={post.seo?.metaDescription ?? post.excerpt}
        exactDocumentTitle={!!post.seo}
        ogImage={ogImage}
        ogImageAlt={
          post.image && !post.image.endsWith('.svg')
            ? t('blog.post.ogImageAlt', { title: post.title })
            : undefined
        }
        ogType="article"
        article={{
          publishedTime: post.date,
          modifiedTime: post.updated,
          author: displayAuthor,
        }}
        jsonLd={jsonLdBlocks}
      />

      <div className="max-w-7xl mx-auto px-6 py-10 lg:py-14">
        <Link
          to={backToBlogTo}
          className="inline-flex items-center gap-2 text-sm font-medium text-[#1769ff] hover:text-blue-700 mb-8 lg:mb-10"
        >
          <i className="ri-arrow-left-line" aria-hidden />
          {t('blog.post.backToBlog')}
        </Link>

        {/* Hero: title block + image */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 lg:items-start mb-10 lg:mb-12">
          <div className="min-w-0">
            <span
              className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-md mb-4 ${postCategoryBadgeClass(post.category)}`}
            >
              {post.category}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-bold text-gray-900 leading-tight tracking-tight mb-6">
              {post.title}
            </h1>
            <div className="flex items-center gap-3 mb-6">
              {post.authorAvatar ? (
                <img
                  src={post.authorAvatar}
                  alt=""
                  className="w-11 h-11 rounded-full object-cover border border-gray-200 shrink-0"
                />
              ) : (
                <div
                  className="w-11 h-11 rounded-full bg-[#eef5ff] text-[#1769ff] flex items-center justify-center text-sm font-bold border border-blue-100 shrink-0"
                  aria-hidden
                >
                  {authorInitials(displayAuthor)}
                </div>
              )}
              <div className="text-sm">
                <p className="font-semibold text-gray-900">{displayAuthor}</p>
                <p className="text-gray-500">
                  {post.updated && post.updated !== post.date ? (
                    <>
                      {t('blog.post.updatedOn', { date: formatDate(post.updated) })}
                      <span className="text-gray-400"> · {t('blog.post.publishedOn', { date: formatDate(post.date) })}</span>
                    </>
                  ) : (
                    formatDate(post.date)
                  )}
                </p>
              </div>
            </div>
            <p className="text-lg text-gray-600 leading-relaxed italic border-l-4 border-gray-200 pl-4">
              {introText}
            </p>
            {isComparison && (
              <p className="mt-4 text-xs text-gray-500 leading-relaxed">
                <i className="ri-information-line mr-1" aria-hidden />
                {t('blog.post.disclosure')}
              </p>
            )}
          </div>

          <div className="w-full">
            <div
              className={`relative overflow-hidden rounded-2xl border border-gray-100 shadow-sm aspect-[4/3] ${
                heroShouldContain ? 'bg-white' : 'bg-gray-100'
              }`}
            >
              {heroInset ? (
                <div className="absolute inset-6 min-h-0 min-w-0 sm:inset-8 md:inset-10">
                  <img
                    src={heroImage}
                    alt=""
                    className="h-full w-full object-contain object-center"
                  />
                </div>
              ) : (
                <img
                  src={heroImage}
                  alt=""
                  className={
                    heroIsSvg
                      ? 'h-full w-full object-contain object-center p-6'
                      : post.heroImageFit === 'contain'
                        ? 'h-full w-full object-contain object-center'
                        : 'h-full w-full object-cover object-center'
                  }
                />
              )}
            </div>
          </div>
        </div>

        {/* Key takeaways */}
        {post.keyTakeaways && post.keyTakeaways.length > 0 && (
          <div className="mb-12 lg:mb-14 rounded-2xl border border-blue-100 bg-blue-50/80 px-6 py-6 lg:px-8 lg:py-7">
            <div className="flex items-center gap-2 text-blue-900 font-semibold mb-5">
              <i className="ri-key-line text-xl text-blue-700" aria-hidden />
              {t('blog.post.keyTakeaways')}
            </div>
            <ul className="space-y-4 text-gray-700 leading-relaxed">
              {post.keyTakeaways.map((item, i) => (
                <li key={i} className="pl-1">
                  <span className="font-bold text-blue-900">{item.lead}</span>{' '}
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Article + TOC */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_240px] gap-10 lg:gap-14">
          <article className="min-w-0">
            <div className="prose prose-gray max-w-none prose-p:text-gray-700 prose-p:leading-[1.75] prose-a:text-[#1769ff]">
              {hasSections
                ? <ArticleSections sections={post.sections ?? []} proofPoint={post.proofPoint} withPitch={isComparison} />
                : post.body?.map((paragraph, i) => (
                    <p key={i} className="text-base text-gray-700 leading-[1.75] mb-5 last:mb-0">
                      {paragraph}
                    </p>
                  ))}

              {post.faq && post.faq.length > 0 && (
                <div className="mt-14 pt-10 border-t border-gray-200">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('common.faqTitle')}</h2>
                  <div className="space-y-4">
                    {post.faq.map((item, i) => (
                      <div
                        key={i}
                        className="p-5 rounded-2xl border border-gray-200 bg-gray-50/80 hover:bg-gray-50 transition"
                      >
                        <h3 className="text-base font-semibold text-gray-900 mb-1">{item.question}</h3>
                        <p className="text-sm text-gray-700 leading-relaxed">{item.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>

          {tocItems.length > 0 && (
            <aside className="lg:sticky lg:top-28 lg:self-start">
              <nav
                className="rounded-2xl border border-gray-200 bg-gray-50/80 px-5 py-5 mb-2 lg:mb-0"
                aria-label={t('blog.post.tocAriaLabel')}
              >
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">{t('blog.post.tocTitle')}</p>
                <ul className="space-y-2.5 text-sm">
                  {tocItems.map((item) => (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        className="text-gray-600 hover:text-[#1769ff] leading-snug block transition-colors"
                      >
                        {item.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>
          )}
        </div>
      </div>

      {/* Related articles */}
      {relatedPosts.length > 0 && (
        <section className="py-14 lg:py-16 bg-[#FAFAFA] border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 lg:mb-10">{t('blog.post.relatedTitle')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {relatedPosts.map((rp) => (
                <Link
                  key={rp.slug}
                  to={`/blog/${rp.slug}`}
                  className="group flex flex-col bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-blue-200 hover:shadow-lg transition-all duration-200"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-gray-100">
                    {rp.image ? (
                      <img
                        src={rp.image}
                        alt=""
                        className={`w-full h-full transition-transform duration-300 group-hover:scale-[1.02] ${
                          rp.image.endsWith('.svg') || rp.heroImageFit === 'contain'
                            ? 'object-contain object-center bg-white'
                            : 'object-cover'
                        }`}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-blue-50 to-white flex items-center justify-center">
                        <i className="ri-article-line text-4xl text-blue-400/60" />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col flex-1 p-5">
                    <span className={`inline-flex self-start text-xs font-semibold px-2.5 py-1 rounded-md mb-3 ${postCategoryBadgeClass(rp.category)}`}>
                      {rp.category}
                    </span>
                    <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-[#1769ff] transition-colors line-clamp-2 leading-snug">
                      {rp.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed line-clamp-2 mb-4 flex-1">{rp.excerpt}</p>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-[#1769ff] mt-auto group-hover:gap-1.5 transition-all">
                      {t('blog.card.readMore')} <i className="ri-arrow-right-line" aria-hidden />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 bg-[#1769ff] mt-8">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">{t('blog.post.cta.title')}</h2>
          <p className="text-base text-white/80 mb-7">{t('blog.post.cta.subtitle')}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://app.geofleet.ai/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white px-6 py-2.5 rounded-xl text-sm font-semibold text-[#1769ff] hover:bg-gray-100 transition-all shadow-lg w-full sm:w-auto justify-center"
            >
              {t('common.ctaStartTrial')}
              <i className="ri-arrow-right-line text-sm" />
            </a>
            <a
              href="https://meetings.hubspot.com/aditya145"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-2 border-white/90 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-white/10 transition-all w-full sm:w-auto justify-center"
            >
              {t('blog.post.cta.bookDemo')}
            </a>
            <Link
              to="/features/ai-agents"
              className="inline-flex items-center gap-2 text-white/95 text-sm font-medium hover:underline"
            >
              {t('blog.post.cta.aiAgentsLink')}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
